import * as React from "react";
import { t } from "i18next";
import { observer } from "mobx-react-lite";
import { Button, Modal, notification, Space, Spin, Switch, Tag } from "antd";
import {
  ArrowsAltOutlined,
  BarsOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  ExportOutlined,
  ImportOutlined,
  LeftOutlined,
  LockOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { onError } from "src/common/onError";
import { ModalDelete } from "src/components/ModalDelete";
import styles from "./Mc2Entity.module.less";
import { Mc2ImageView } from "./Mc2ImageView";
import { Mc2Table } from "./Mc2Table";
import { Mc2PointSelectModal } from "./Mc2PointSelect";
import { Mc2EntityParam } from "./Mc2EntityParam";
import { mc2SummaryStore as summaryStore } from "../mc2SummaryStore";
import { Mc2EntityStore } from "./Mc2EntityStore";
import { Mc2EntityComment } from "./Mc2EntityComment";
import { Mc2EntitySizeLine } from "./Mc2EntitySizeLine";
import { Mc2EntityBaseSize } from "./Mc2EntityBaseSize";
import { Mc2PointsOrderModal } from "./Mc2PointsOrderModal";
import { Mc2ImportModal } from "./Mc2ImportModal";
import { urlMc2 } from "../../apiMc2";

interface PropsMc2Entity {
  store: Mc2EntityStore;
}

export const Mc2Entity: React.FC<PropsMc2Entity> = observer(({ store }) => {
  const { id: mcEntityId, name, stateId } = store.mcEntity;
  const { readOnly } = store;
  const state = stateId && summaryStore.statesDict[stateId];
  React.useEffect(() => {
    store.init().catch(onError);
  }, [mcEntityId]);

  const scaleConfirm = () => {
    Modal.confirm({
      icon: <ExclamationCircleFilled />,
      title: t("Overwrite measurement chart changes?"),
      content: t(
        "Scaling will change the dimension values relative to the base size.",
      ),
      okText: t("Overwrite"),
      okButtonProps: { danger: true },
      onOk: () => {
        store
          .scaling()
          .then(() =>
            notification.success({ message: "Выполнено масштабирование" }),
          )
          .catch(onError);
      },
    });
  };

  return (
    <div className={styles.frame}>
      <div className={styles.topHeader}>
        <div className={styles.titleBox}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => summaryStore.closeEntity()}
          />
          <div className={styles.title}>{name}</div>
          {state && <Tag color="gold">{state.name}</Tag>}
          {readOnly && <LockOutlined title="Нет прав на редактирование" />}
        </div>

        {!readOnly && (
          <Button
            icon={<ImportOutlined />}
            disabled={store.buzy}
            onClick={() => store.setMode("import")}
          >
            Импорт
          </Button>
        )}
        <Button
          icon={<ExportOutlined />}
          href={`${urlMc2("/mapping/export")}?mcEntityId=${encodeURIComponent(mcEntityId)}`}
          target="_blank"
        >
          Экспорт таблицы
        </Button>
      </div>
      <div className={styles.entityParams}>
        <Mc2EntityParam label="Размерный ряд">
          <Mc2EntitySizeLine store={store} />
        </Mc2EntityParam>
        <Mc2EntityParam label="Базовый размер">
          <Mc2EntityBaseSize store={store} />
        </Mc2EntityParam>
        <Mc2EntityParam label="Комментарий">
          <Mc2EntityComment store={store} />
        </Mc2EntityParam>
      </div>
      <div className={styles.tools}>
        <Space style={{ flex: 1 }}>
          <Switch
            value={store.isImagesVisible}
            onChange={(v) => store.setImageVisible(v)}
          />
          <span>Показать схему MC</span>
        </Space>
        {!readOnly && (
          <>
            <Button
              icon={<DeleteOutlined />}
              danger
              disabled={store.isDisabled("delete")}
              title={store.getReason("delete")}
              loading={store.taskType === "delete"}
              onClick={() => store.startDeleteRows()}
            >
              Удалить
            </Button>
            <Button
              icon={<BarsOutlined />}
              disabled={store.buzy}
              onClick={() => store.setMode("pointsOrder")}
            >
              Изменить порядок
            </Button>
            <Button
              icon={<ArrowsAltOutlined />}
              disabled={store.isDisabled("scaling")}
              title={store.getReason("scaling")}
              loading={store.taskType === "scaling"}
              onClick={scaleConfirm}
            >
              {t("Scale")}
            </Button>
            <Button
              icon={<PlusCircleOutlined />}
              disabled={store.buzy}
              onClick={() => {
                store.startCreateFromLib();
              }}
            >
              Выбрать из библиотеки
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                store
                  .createPoint()
                  .then(() =>
                    notification.success({
                      message: "Выполнено создание новой строки",
                    }),
                  )
                  .catch(onError);
              }}
              loading={store.taskType === "createEmpty"}
              disabled={store.isDisabled("createEmpty")}
            >
              Создать point
            </Button>
          </>
        )}
      </div>

      <div className={styles.mainContent}>
        {store.buzy && (
          <div className={styles.spin}>
            <Spin size="large" />
          </div>
        )}
        <Mc2Table store={store} />
        {store.isImagesVisible && <Mc2ImageView store={store} />}
      </div>
      <ModalDelete
        message="Удалить выбранные строки?"
        open={store.mode === "delete"}
        onCancel={() => store.setMode(null)}
        onOk={() => {
          store
            .finalDelete()
            .then(msgSuccess("Выполнено удаление строк"))
            .catch(onError);
        }}
        waiting={store.taskType === "delete"}
      />
      <Mc2PointSelectModal
        multiple
        title="Выбрать из библиотеки"
        okText="Выбрать"
        open={store.mode === "pointsLib"}
        close={() => store.setMode(null)}
        settings={summaryStore.settings}
        success={(points) => {
          store
            .createPoints(points)
            .then(msgSuccess("Добавлены точки из библиотеки"))
            .catch(onError);
        }}
      />
      <Mc2PointsOrderModal store={store} />
      <Mc2ImportModal store={store} />
    </div>
  );
});

const msgSuccess = (message: string) => () => notification.success({ message });
